<script setup lang="ts">
/**
 * 起動時の再送キュー回収 (§4.4)。前回タブ閉鎖/クラッシュで送信しきれなかった
 * 変更を検出し、サーバ現値の updatedBy と照合して 復元 or 破棄 を提案する。
 * 自動適用はしない。
 *
 * 照合は updatedAt ではなく updatedBy で行う — サーバ確定値 (serverTimestamp)
 * と baseUpdatedAt (2回目以降の flush はクライアント楽観値 new Date().toISOString())
 * は形式的に一致しないため、自分しか書いていなくてもほぼ常に不一致になり
 * 正当な復元まで破棄してしまう。updatedBy 照合は「不一致 = 他者が書いた」
 * という設計意図をそのまま実装する。
 */
import { ref, watch } from 'vue';
import { createResendQueue, type ResendEntry } from '@/lib/firebase/resend-queue';
import { getFirebase, isFirebaseConfigured } from '@/lib/firebase/init';
import { useAuthStore } from '@/stores/authStore';

const auth = useAuthStore();
const pending = ref<ResendEntry[]>([]);
const open = ref(false);
const busy = ref(false);
const restoreError = ref(false);

/** 多重実行ガード: checking 中は再実行しない。processed は非 ready へ抜けた時点でリセットし、
 *  同一 ready 中の二重実行は防ぎつつ、次の ready 遷移 (再ログイン等) では必ず取り直す
 *  (古いスナップショット問題の解消)。
 */
let checking = false;
let processed = false;

// onMounted 一発だと、マウント時点で未サインインだった場合 (対話的サインイン後に
// ready へ遷移するケース) に本処理が一度も走らない。status の遷移を継続監視し、
// ready へ遷移するたびに (再ログイン等で) pending を取り直す。
watch(
  () => auth.status,
  (status) => {
    if (!isFirebaseConfigured()) return;
    if (status !== 'ready' || !auth.email) {
      processed = false;
      return;
    }
    if (checking || processed) return;
    checking = true;
    try {
      const queue = createResendQueue(auth.email);
      const entries = queue.entries();
      processed = true;
      if (entries.length > 0) {
        pending.value = entries;
        open.value = true;
      }
    } finally {
      checking = false;
    }
  },
  { immediate: true },
);

async function restore(): Promise<void> {
  busy.value = true;
  restoreError.value = false;
  const email = auth.email!;
  const queue = createResendQueue(email);
  try {
    const { db } = await getFirebase();
    const { doc, getDoc, updateDoc, serverTimestamp } = await import('firebase/firestore');
    for (const e of pending.value) {
      const [col, id] = e.docPath.split('/') as [string, string];
      try {
        const snap = await getDoc(doc(db, col, id));
        const data = snap.exists() ? (snap.data() as { updatedBy?: string }) : null;
        if (data === null) {
          // doc が削除済み → 復元先が無い。エントリは破棄
          queue.ack(e.docPath);
          continue;
        }
        if (data.updatedBy === email) {
          // 最終書込者が自分 → 自分の未送信分の再送は安全
          await updateDoc(doc(db, col, id), {
            ...e.payload,
            updatedAt: serverTimestamp(),
            updatedBy: email,
          });
          queue.ack(e.docPath);
        } else {
          // 最終書込者が他者 → 盲目的再送はしない (§4.4)。破棄
          // (§5 の競合トースト実装は P6 — ここでは安全側に倒す)
          queue.ack(e.docPath);
        }
      } catch {
        // このエントリだけ失敗 — ack せず次回起動時に再提案。他エントリの処理は継続
        restoreError.value = true;
      }
    }
  } catch {
    // getFirebase/import 自体の失敗 — 全エントリ未処理のまま次回起動へ持ち越し
    restoreError.value = true;
  } finally {
    busy.value = false;
    open.value = false;
  }
}

function discard(): void {
  const queue = createResendQueue(auth.email!);
  for (const e of pending.value) queue.ack(e.docPath);
  open.value = false;
}
</script>

<template>
  <v-dialog
    v-model="open"
    max-width="480"
    persistent
  >
    <v-card>
      <v-card-title>未保存の変更があります</v-card-title>
      <v-card-text>
        前回のセッションで保存しきれなかった変更が {{ pending.length }} 件あります。
        復元しますか？ (他のメンバーが後から更新した物件は安全のため破棄されます)
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          variant="text"
          :disabled="busy"
          @click="discard"
        >
          破棄
        </v-btn>
        <v-btn
          color="primary"
          :loading="busy"
          :disabled="busy"
          @click="restore"
        >
          復元する
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-snackbar
    v-model="restoreError"
    color="error"
    :timeout="6000"
  >
    一部の変更を復元できませんでした (次回起動時に再試行できます)
  </v-snackbar>
</template>
